import { CompanyReference } from './contacts/CompanyStore';
import { TeamMemberReference } from './TeamMemberStore';
import { ProductReference } from './products/ProductStore';
import { DateTime } from './DateTime';
import { SaleReference } from './SaleStore';
import {
  types,
  Instance,
  SnapshotIn,
  SnapshotOut,
  getRoot,
  IAnyModelType,
  flow,
  getParent,
  getSnapshot,
} from 'mobx-state-tree';
import { modelWithCRUD } from './utils/modelWithCRUD';
import { ResponsibleMixin, CommomMixin } from './Mixins';
import { ContactReference, IContact } from './contacts/ContactStore';
import { RootModelType } from './Root';

export enum TaskStatus {
  CREATED = 'created',
  PROCESSING = 'processing',
  PAUSED = 'paused',
  DONE = 'done',
}

export const TaskLog = types.model('TaskLog', {
  id: types.union(types.identifier, types.identifierNumber),
  task: types.late(() => TaskReference),
  logged_by: types.late(() => TeamMemberReference),
  created_at: DateTime,
  note: types.maybeNull(types.string),
  duration: types.number, // Duration in milliseconds
});

export const TaskLogStore = modelWithCRUD('task-logs', TaskLog).named(
  'TaskLogStore'
);

export interface ITaskLog extends Instance<typeof TaskLog> {}
export interface ITaskLogSnapshotIn extends SnapshotIn<typeof TaskLog> {}
export interface ITaskLogSnapshotOut extends SnapshotOut<typeof TaskLog> {}

export const TaskLogReference = types.reference(
  types.late((): IAnyModelType => TaskLog),
  {
    get(identifier, parent): any {
      return (getRoot(parent!) as RootModelType).taskLogStore.getItemNaive(
        identifier
      );
    },
    set(value) {
      return value.id;
    },
  }
);

export const Task = types
  .compose(
    CommomMixin,
    ResponsibleMixin,
    types
      .model('Task', {
        id: types.union(types.identifier, types.identifierNumber),
        status: types.enumeration<TaskStatus>(Object.values(TaskStatus)),
        title: types.string,
        description: types.maybeNull(types.string),
        service: types.late(() => ProductReference),

        budget: types.maybeNull(types.number),

        logs: types.array(types.late(() => TaskLogReference)),

        customer: types.late(() => CompanyReference), // Notice, customer is provided by API and cannot be changed after creation
        sale: types.maybeNull(types.late(() => SaleReference)),
        contacts: types.array(types.late(() => ContactReference)),

        due_date: types.maybeNull(DateTime),
      })
      .views((self) => ({
        get timeSpent(): number | null {
          if (self.logs.length === 0) return null;
          if (!self.logs.reduce((p, n) => p && !!n, true)) return null;
          return self.logs.reduce((p, log: ITaskLog) => p + log.duration, 0);
        },
      }))
  )
  .views((self) => ({
    get overdue(): boolean {
      if (!self.due_date) return false;
      return (
        self.status !== TaskStatus.DONE &&
        self.due_date < new Date(Date.now() + 24 * 1000 * 60 * 60)
      );
    },
    /**
     * Computes if the budget is in danger.
     * This is true for situations where we have "not done enough yet".
     * Computed by a linear burndown from creation date till due date.
     * TODO: Move this to the api...
     */
    get budgetInDanger(): boolean {
      if (!self.due_date) return false;
      if (self.status === TaskStatus.DONE) return false;
      if (!self.budget) return false;

      const duration = self.due_date.getTime() - self.created_at.getTime();
      // We must ensure to clip time left to 0, as it might be negative
      // in case of an overdue task
      const timeLeft = Math.max(self.due_date.getTime() - Date.now(), 0);

      // Compute the expected work done and actual work done
      const expected = timeLeft / duration;
      const actual = (self.timeSpent || 0) / (self.budget * 60 * 60 * 1000);
      // We are in danger if we are 10% above burndown.
      return (expected - actual) / actual >= 0.1;
    },
    get overBudget(): boolean {
      if (!self.budget) return false;
      if (!self.timeSpent) return false;
      // Remember, budget is in hours, whereas timeSpent is in milliseconds
      return self.timeSpent > self.budget * 60 * 60 * 1000;
    },
  }))
  .actions((self) => ({
    removeContact: flow(function* removeContact(contact: IContact) {
      const index = self.contacts.findIndex(
        (c: any) => c && c.id === contact.id
      );
      if (index > -1) {
        const [removed] = self.contacts.splice(index, 1);
        // Parrent is TaskStore
        try {
          const parent = getParent(self, 2) as any;
          yield parent.updateItem(self as any, {
            contacts: getSnapshot(self).contacts,
          });
        } catch {
          // Undo...
          self.contacts.splice(index, 0, removed);
        }
      }
    }),
    addContact: flow(function* addContact(contact: IContact) {
      const index = self.contacts.findIndex((c) => c && c.id === contact.id);
      if (index === -1) {
        const parent = getParent(self, 2) as any;
        self.contacts.push(contact);
        try {
          yield parent.updateItem(self as any, {
            contacts: getSnapshot(self).contacts,
          });
        } catch {
          self.contacts.pop();
        }
      }
    }),
  }));

export const TaskStore = modelWithCRUD('tasks', Task).named('TaskStore');

export interface ITask extends Instance<typeof Task> {}
export interface ITaskSnapshotIn extends SnapshotIn<typeof Task> {}
export interface ITaskSnapshotOut extends SnapshotOut<typeof Task> {}

export const TaskReference = types.reference(
  types.late((): IAnyModelType => Task),
  {
    get(identifier, parent): any {
      return (getRoot(parent!) as RootModelType).taskStore.getItemNaive(
        identifier
      );
    },
    set(value) {
      return value.id;
    },
  }
);
